import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import axios from 'axios';
import ExcelJS from 'exceljs';
import ResponsiveTable from './ResponsiveTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faChartSimple,
  faDiagramProject,
  faFileExcel,
} from '@fortawesome/free-solid-svg-icons';
import Chart from 'react-apexcharts';

function groupTopPlatforms(items, n = 7) {
  const sorted = [...items].sort((a, b) => b.platformAmount - a.platformAmount);
  if (sorted.length <= n) return sorted;
  const etcAmount = sorted.slice(n).reduce((s, r) => s + r.platformAmount, 0);
  return [...sorted.slice(0, n), { platformType: '기타', platformAmount: etcAmount }];
}

const PLATFORM_HORIZ_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  plotOptions: { bar: { horizontal: true, borderRadius: 4, dataLabels: { position: 'end' } } },
  xaxis: { categories, labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { style: { fontSize: '12px', colors: '#94a3b8' }, maxWidth: 130 } },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetX: -6,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 0, right: 70 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
});

const UNIFIED_BAR_OPTIONS = (categories, id) => ({
  chart: { id, toolbar: { show: false }, background: 'transparent' },
  theme: { mode: 'dark' },
  xaxis: {
    categories,
    labels: { style: { fontSize: '12px', colors: '#94a3b8' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => val.toLocaleString(),
    offsetY: -20,
    style: { fontSize: '11px', colors: ['#94a3b8'] },
  },
  plotOptions: {
    bar: { dataLabels: { position: 'top' }, borderRadius: 4 },
  },
  fill: { colors: ['#0ea5e9'] },
  grid: { borderColor: 'rgba(255,255,255,0.05)', padding: { left: 10, right: 0 } },
  tooltip: { theme: 'dark', y: { formatter: (val) => val.toLocaleString() } },
  responsive: [
    {
      breakpoint: 640,
      options: {
        chart: { height: 250, toolbar: { show: false } },
        yaxis: [{ labels: { show: false }, axisTicks: { show: false }, axisBorder: { show: false } }],
        grid: { padding: { left: 24 } },
      },
    },
  ],
});

const ChartLoading = () => (
  <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-16">
    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-sky-500" />
    <span>불러오는 중...</span>
  </div>
);

function ByClientLatestMonthTable({
  data = [],
  isLatestMonth,
  onUpdate,
  totalCount,
  currentPage,
  pageSize,
  isSuperAdmin: isSuperAdminProp = false,
}) {
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  // prop이 잘못 전달되더라도 store에서 직접 확인
  const isSuperAdmin = isSuperAdminProp || me?.superAdminYn === 'Y';

  const [selectedClient, setSelectedClient] = useState(null);
  const [channelAmounts, setChannelAmounts] = useState([]);
  const [selectedPlatformClient, setSelectedPlatformClient] = useState(null);
  const [platformAmounts, setPlatformAmounts] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [editingPrev, setEditingPrev] = useState(null); // { clientId, calculateMonth, value }
  const [editingAdvance, setEditingAdvance] = useState(null); // { clientId, calculateMonth, value }

  const handlePrevAmountEdit = useCallback(async (clientId, calculateMonth, value) => {
    try {
      await axios.put('/ByClient/byClientLatestMonth/setPrevAmount', {
        clientId,
        calculateMonth,
        prevAmount: parseInt(value, 10) || 0,
      });
      setEditingPrev(null);
      onUpdate?.();
    } catch (error) {
      alert('수정 중 오류가 발생했습니다.');
    }
  }, [onUpdate]);

  const handleReverseCarryover = useCallback(async (clientId, calculateMonth) => {
    const isConfirmed = window.confirm('이월 소급처리 하시겠습니까?\n다음 달의 전월이월액이 0으로 초기화됩니다.');
    if (!isConfirmed) return;
    try {
      await axios.put('/ByClient/byClientLatestMonth/reverseCarryover', { clientId, calculateMonth });
      onUpdate?.();
    } catch (error) {
      alert('소급처리 중 오류가 발생했습니다.');
    }
  }, [onUpdate]);

  const handleAdvanceAmountEdit = useCallback(async (clientId, calculateMonth, value) => {
    try {
      await axios.put('/ByClient/byClientLatestMonth/setAdvanceAmount', {
        clientId,
        calculateMonth,
        advanceAmount: parseInt(value, 10) || 0,
      });
      setEditingAdvance(null);
      onUpdate?.();
    } catch (error) {
      alert('수정 중 오류가 발생했습니다.');
    }
  }, [onUpdate]);

  const handleChannelChart = useCallback(async (clientId, clientName) => {
    try {
      setSelectedClient({ clientId, clientName });
      setChannelAmounts([]);
      setLoadingChart(true);
      const res = await axios.get('/ByClient/getMonthlyClientAmounts', {
        params: { clientId },
      });
      setChannelAmounts(res.data);
    } catch (error) {
      console.error('최근 1년 정산 데이터 오류:', error);
    } finally {
      setLoadingChart(false);
    }
  }, []);

  const handleExcelDownload = useCallback(async () => {
    const { clientId, clientName, calculateMonth } = selectedPlatformClient;
    try {
      const res = await axios.get('/ByPlatform/excelDataByClient', {
        params: { clientId, calculateMonth },
      });
      const { rows } = res.data;
      if (!rows || rows.length === 0) { alert('데이터가 없습니다.'); return; }

      // ── 스타일 상수 ──────────────────────────────────────────────────────
      const C = {
        PLATFORM_FILL: { type:'pattern', pattern:'solid', fgColor:{ argb:'FF1E3A5F' } },
        HEADER_FILL:   { type:'pattern', pattern:'solid', fgColor:{ argb:'FF2D5986' } },
        ALBUM_FILL:    { type:'pattern', pattern:'solid', fgColor:{ argb:'FFE8E8E8' } },
        SUB_FILL:      { type:'pattern', pattern:'solid', fgColor:{ argb:'FF2D5986' } },
        TOTAL_FILL:    { type:'pattern', pattern:'solid', fgColor:{ argb:'FF1E3A5F' } },
        WHITE: { argb:'FFFFFFFF' },
        BLACK: { argb:'FF000000' },
        LIGHT: { argb:'FFCBD5E1' },
        BORDER: {
          top:    { style:'thin', color:{ argb:'FF334155' } },
          left:   { style:'thin', color:{ argb:'FF334155' } },
          bottom: { style:'thin', color:{ argb:'FF334155' } },
          right:  { style:'thin', color:{ argb:'FF334155' } },
        },
        NUM: '#,##0',
      };

      const styleCell = (cell, opts = {}) => {
        if (opts.fill)   cell.fill   = opts.fill;
        if (opts.font)   cell.font   = { size: opts.size || 10, ...opts.font };
        if (opts.align)  cell.alignment = { vertical:'middle', ...opts.align };
        if (opts.border !== false) cell.border = C.BORDER;
        if (opts.numFmt) cell.numFmt = opts.numFmt;
      };

      // ── 플랫폼 그룹 + 정렬 ──────────────────────────────────────────────
      const DOMESTIC_ORDER = ['멜론', '바이브', '벅스', '지니', '플로'];
      const platformGroups = {};
      rows.forEach((r) => {
        const pg = DOMESTIC_ORDER.includes(r.platform) ? r.platform : '해외';
        if (!platformGroups[pg]) platformGroups[pg] = [];
        platformGroups[pg].push(r);
      });
      const orderedGroups = [
        ...DOMESTIC_ORDER.filter((p) => platformGroups[p]),
        ...(platformGroups['해외'] ? ['해외'] : []),
      ];

      // ── 컬럼 정의 ─────────────────────────────────────────────────────
      const COLS = [
        { key:'media',          header:'MEDIA',      width:10 },
        { key:'channel',        header:'채널',        width:20 },
        { key:'calculateMonth', header:'정산월',      width:12 },
        { key:'serviceMonth',   header:'서비스월',    width:12 },
        { key:'albumName',      header:'앨범명',      width:35 },
        { key:'albumCode',      header:'앨범코드',    width:12 },
        { key:'songName',       header:'트랙명',      width:45 },
        { key:'songCode',       header:'트랙코드',    width:12 },
        { key:'artistName',     header:'아티스트명',  width:16 },
        { key:'streaming',      header:'스트리밍',    width:14 },
        { key:'download',       header:'다운로드',    width:12 },
        { key:'amount',         header:'저작인접권료', width:16 },
        { key:'fee',            header:'유통수수료',   width:14 },
        { key:'royalty',        header:'인세',         width:16 },
      ];
      const NUM_COLS = new Set(['streaming','download','amount','fee','royalty']);
      const MERGE_END = 9; // MEDIA(1)~아티스트명(9) 소계 병합 범위

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('플랫폼별정산');
      COLS.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

      let curRow = 1;

      const addRow = (values, opts = {}) => {
        const r = ws.getRow(curRow++);
        r.height = opts.height || 18;
        values.forEach((v, i) => {
          const cell = r.getCell(i + 1);
          cell.value = v;
          styleCell(cell, {
            fill: opts.fill,
            font: { bold: opts.bold, color: opts.color || C.BLACK, size: opts.size || 10 },
            align: { horizontal: opts.center ? 'center' : (NUM_COLS.has(COLS[i]?.key) ? 'right' : 'left') },
            numFmt: NUM_COLS.has(COLS[i]?.key) ? C.NUM : undefined,
          });
        });
        return r;
      };

      // ── 플랫폼별 섹션 ────────────────────────────────────────────────────
      let totalStreaming = 0, totalDownload = 0, totalAmount = 0, totalFee = 0, totalRoyalty = 0;

      for (const pgName of orderedGroups) {
        const pgRows = platformGroups[pgName];

        // 플랫폼 섹션 헤더 (크게)
        const phRow = ws.getRow(curRow++);
        phRow.height = 30;
        ws.mergeCells(curRow - 1, 1, curRow - 1, COLS.length);
        const phCell = phRow.getCell(1);
        phCell.value = pgName;
        styleCell(phCell, { fill: C.PLATFORM_FILL, font: { bold: true, size: 16, color: C.WHITE }, align: { horizontal: 'left' }, border: false });

        // 컬럼 헤더 행 (중앙정렬)
        addRow(COLS.map((c) => c.header), { fill: C.HEADER_FILL, bold: true, color: C.LIGHT, height: 20, center: true });

        // 앨범별 그룹화
        const albumOrder = [];
        const albumRowMap = {};
        pgRows.forEach((r) => {
          if (!albumRowMap[r.albumId]) { albumRowMap[r.albumId] = []; albumOrder.push(r.albumId); }
          albumRowMap[r.albumId].push(r);
        });

        let pgStreaming = 0, pgDownload = 0, pgAmount = 0, pgFee = 0, pgRoyalty = 0;

        for (const albumId of albumOrder) {
          const albumRows = albumRowMap[albumId];
          let albS = 0, albD = 0, albA = 0, albF = 0, albR = 0;

          albumRows.forEach((r) => {
            addRow([
              'DIGITAL', r.channel, r.calculateMonth, r.serviceMonth,
              r.albumName, r.albumCode, r.songName, r.songCode,
              r.artistName,
              r.streaming, r.download,
              r.amount, r.fee, r.royalty,
            ], { color: C.BLACK });
            albS += r.streaming; albD += r.download;
            albA += r.amount;    albF += r.fee; albR += r.royalty;
          });

          // 앨범 소계 — MEDIA~아티스트명 병합 + 옅은 그레이 볼드
          addRow([
            `[소계] ${albumRows[0].albumName}`, '', '', '', '', '', '', '', '',
            albS, albD, albA, albF, albR,
          ], { fill: C.ALBUM_FILL, bold: true, color: C.BLACK });
          ws.mergeCells(curRow - 1, 1, curRow - 1, MERGE_END);

          pgStreaming += albS; pgDownload += albD;
          pgAmount   += albA; pgFee      += albF; pgRoyalty  += albR;
        }

        // 플랫폼 합계 행
        addRow([
          `${pgName} 합계`, '', '', '', '', '', '', '', '',
          pgStreaming, pgDownload, pgAmount, pgFee, pgRoyalty,
        ], { fill: C.SUB_FILL, bold: true, color: C.WHITE, size: 11, height: 22 });
        ws.mergeCells(curRow - 1, 1, curRow - 1, MERGE_END);

        totalStreaming += pgStreaming; totalDownload += pgDownload;
        totalAmount   += pgAmount;    totalFee      += pgFee; totalRoyalty  += pgRoyalty;

        curRow++; // 빈 행 구분
      }

      // ── 총 합계 ──────────────────────────────────────────────────────────
      curRow++;
      addRow([
        '총 합계', '', '', '', '', '', '', '', '',
        totalStreaming, totalDownload, totalAmount, totalFee, totalRoyalty,
      ], { fill: C.TOTAL_FILL, bold: true, color: C.WHITE, size: 13, height: 26 });
      ws.mergeCells(curRow - 1, 1, curRow - 1, MERGE_END);

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientName}_${calculateMonth.substring(0, 7)}_플랫폼별정산.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('엑셀 다운로드 오류:', err);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  }, [selectedPlatformClient]);

  const handlePlatformChart = useCallback(
    async (clientId, clientName, calculateMonth) => {
      try {
        setSelectedPlatformClient({ clientId, clientName, calculateMonth });
        setPlatformAmounts([]);
        setLoadingChart(true);
        const res = await axios.get('/ByPlatform/byMonthPlatformsByClient', {
          params: { clientId, calculateMonth },
        });
        setPlatformAmounts(res.data.items);
      } catch (error) {
        console.error('플랫폼별 정산 데이터 오류:', error);
      } finally {
        setLoadingChart(false);
      }
    },
    []
  );

  const columns = useMemo(
    () => {
      const hasPhysical = data.some((r) => Number(r.physicalAmount) > 0);
      const hasCommission = data.some((r) => Number(r.commissionShareAmount) > 0);
      return [
      {
        Header: () => <div className="whitespace-nowrap text-center">순번</div>,
        id: 'index',
        accessor: (_row, i) => totalCount - ((currentPage - 1) * pageSize + i),
        Cell: ({ value }) => <div className="text-center">{value}</div>,
      },
      {
        Header: () => <div className="text-center whitespace-nowrap">연간</div>,
        id: 'channel',
        Cell: ({ row }) => (
          <span
            className="text-sky-500 text-xl cursor-pointer whitespace-nowrap"
            title="최근 1년 채널별 정산 보기"
            onClick={() =>
              handleChannelChart(
                row.original.clientId,
                row.original.client.name
              )
            }>
            <FontAwesomeIcon icon={faChartSimple} />
          </span>
        ),
      },
      {
        Header: () => <div className="text-center whitespace-nowrap">채널</div>,
        id: 'platform',
        Cell: ({ row }) => (
          <span
            className="text-purple-500 text-xl cursor-pointer whitespace-nowrap"
            title="해당월 플랫폼별 정산 보기"
            onClick={() =>
              handlePlatformChart(
                row.original.clientId,
                row.original.client.name,
                row.original.calculateMonth
              )
            }>
            <FontAwesomeIcon icon={faDiagramProject} />
          </span>
        ),
      },
      {
        Header: '권리사',
        accessor: 'client.name',
        Cell: ({ row }) => (
          <span
            className="font-bold text-sky-600 hover:cursor-pointer text-sm"
            onClick={() =>
              navigate(
                `/ByClientMonthAlbums?clientId=${row.original.clientId}&calculateMonth=${row.original.calculateMonth}`
              )
            }>
            {row.original.client.name}
          </span>
        ),
      },
      {
        Header: '인접권료',
        accessor: 'amount',
        Cell: ({ value }) => <b>{Number(value).toLocaleString()}</b>,
      },
      {
        Header: '유통수수료',
        accessor: 'feeAmount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '인세총액',
        accessor: 'receiveAmount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      ...(hasPhysical ? [{
        Header: '음반정산',
        accessor: 'physicalAmount',
        Cell: ({ value }) =>
          value > 0 ? Number(value).toLocaleString() : <span className="text-slate-600">-</span>,
      }] : []),
      ...(hasCommission ? [{
        Header: '별도정산',
        accessor: 'commissionShareAmount',
        Cell: ({ value }) =>
          value > 0 ? Number(value).toLocaleString() : <span className="text-slate-600">-</span>,
      }] : []),
      {
        Header: '전월이월',
        accessor: 'prevAmount',
        Cell: ({ value, row }) => {
          const { clientId, calculateMonth } = row.original;
          const key = `${clientId}-${calculateMonth}`;
          if (editingPrev?.key === key) {
            return (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="number"
                  className="w-24 px-1 py-0.5 rounded bg-slate-700 text-white text-sm border border-sky-500 focus:outline-none"
                  value={editingPrev.value}
                  autoFocus
                  onChange={(e) => setEditingPrev((p) => ({ ...p, value: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handlePrevAmountEdit(clientId, calculateMonth, editingPrev.value);
                    if (e.key === 'Escape') setEditingPrev(null);
                  }}
                />
                <button
                  className="text-xs text-sky-400 hover:text-sky-200"
                  onClick={() => handlePrevAmountEdit(clientId, calculateMonth, editingPrev.value)}>
                  저장
                </button>
                <button
                  className="text-xs text-slate-400 hover:text-white"
                  onClick={() => setEditingPrev(null)}>
                  취소
                </button>
              </div>
            );
          }
          return (
            <span
              className="cursor-pointer hover:text-sky-400 hover:underline"
              title="클릭하여 전월이월 수정"
              onClick={() => setEditingPrev({ key, clientId, calculateMonth, value: String(value) })}>
              {Number(value).toLocaleString()}
            </span>
          );
        },
      },
      {
        Header: '선급',
        accessor: 'advanceAmount',
        Cell: ({ value, row }) => {
          const { clientId, calculateMonth } = row.original;
          const key = `${clientId}-${calculateMonth}`;
          const current = value || 0;
          if (editingAdvance?.key === key) {
            return (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="number"
                  className="w-24 px-1 py-0.5 rounded bg-slate-700 text-white text-sm border border-amber-500 focus:outline-none"
                  value={editingAdvance.value}
                  autoFocus
                  onChange={(e) => setEditingAdvance((p) => ({ ...p, value: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdvanceAmountEdit(clientId, calculateMonth, editingAdvance.value);
                    if (e.key === 'Escape') setEditingAdvance(null);
                  }}
                />
                <button
                  className="text-xs text-amber-400 hover:text-amber-200"
                  onClick={() => handleAdvanceAmountEdit(clientId, calculateMonth, editingAdvance.value)}>
                  저장
                </button>
                <button
                  className="text-xs text-slate-400 hover:text-white"
                  onClick={() => setEditingAdvance(null)}>
                  취소
                </button>
              </div>
            );
          }
          return (
            <span
              className={`cursor-pointer hover:text-amber-400 hover:underline ${current > 0 ? 'text-rose-400' : ''}`}
              title="클릭하여 선급 지급액 수정"
              onClick={() => setEditingAdvance({ key, clientId, calculateMonth, value: String(current) })}>
              {current > 0 ? `-${Number(current).toLocaleString()}` : <span className="text-slate-600">-</span>}
            </span>
          );
        },
      },
      {
        Header: '지급총액',
        id: 'combinedTotal',
        Cell: ({ row }) => {
          const total = (row.original.totalAmount || 0) + (row.original.physicalAmount || 0);
          return <b>{Number(total).toLocaleString()}</b>;
        },
      },
      {
        Header: '차월이월',
        accessor: 'nextAmount',
        Cell: ({ value }) => <b>{Number(value).toLocaleString()}</b>,
      },
      {
        Header: '강제처리',
        id: 'action',
        Cell: ({ row }) => {
          const { nextAmount, totalAmount, calculateMonth, clientId, amount, prevAmount, nextMonthPrevAmount = 0, nextMonthExists = false } =
            row.original;
          const toNextAmountBtn = async () => {
            const isConfirmed = window.confirm('임의조정 하시겠습니까?');
            if (!isConfirmed) return;
            const receiveAmount = amount + prevAmount;
            try {
              await axios.put('/ByClient/byClientLatestMonth/toNextAmount', {
                clientId,
                calculateMonth,
                receiveAmount,
                nextAmount,
              });
              onUpdate?.();
            } catch (error) {
              alert('수정 중 오류가 발생했습니다.');
            }
          };

          // 슈퍼관리자가 아닌 경우 과거월은 정산완료 표시
          if (!isLatestMonth && !isSuperAdmin) {
            return (
              <div className="px-2 py-1 rounded-lg bg-white/10 text-sm text-slate-400 font-bold inline-block">
                정산완료
              </div>
            );
          }

          // 다음달 데이터가 업로드된 경우 슈퍼관리자 외 버튼 잠금
          if (nextMonthExists && !isSuperAdmin) {
            return (
              <div className="px-2 py-1 rounded-lg bg-white/10 text-sm text-slate-400 font-bold inline-block">
                정산완료
              </div>
            );
          }

          // 슈퍼관리자: 다음달에 전월이월이 있고 nextAmount=0이면 (totalAmount 값 무관) 지급처리(소급)
          if (isSuperAdmin && nextMonthPrevAmount > 0 && nextAmount === 0) {
            return (
              <div
                className="px-2 py-1 rounded-lg hover:cursor-pointer bg-sky-500/20 hover:bg-sky-500/30 text-sm text-sky-400 font-bold inline-block"
                onClick={() => handleReverseCarryover(clientId, calculateMonth)}>
                지급처리
              </div>
            );
          }

          if (nextAmount === 0 && totalAmount === 0) {
            return (
              <div className="px-2 py-1 rounded-lg bg-white/10 text-sm text-slate-400 font-bold inline-block">
                이월확정
              </div>
            );
          }
          // 이월처리: totalAmount > 0, nextAmount = 0 → 이월 전 상태
          if (nextAmount === 0) {
            return (
              <div
                className="px-2 py-1 rounded-lg hover:cursor-pointer bg-rose-500/20 hover:bg-rose-500/30 text-sm text-rose-400 font-bold inline-block"
                onClick={toNextAmountBtn}>
                이월처리
              </div>
            );
          }
          // 지급처리: 슈퍼관리자는 nextAmount > 0이면 소급 가능, 일반은 10000 이상만
          if (isSuperAdmin ? nextAmount > 0 : nextAmount >= 10000) {
            return (
              <div
                className="px-2 py-1 rounded-lg hover:cursor-pointer bg-sky-500/20 hover:bg-sky-500/30 text-sm text-sky-400 font-bold inline-block"
                onClick={toNextAmountBtn}>
                지급처리
              </div>
            );
          }
          return (
            <div className="px-2 py-1 rounded-lg bg-white/10 text-sm text-slate-400 font-bold inline-block">
              이월확정
            </div>
          );
        },
      },
    ];
    },
    [
      data,
      isLatestMonth,
      isSuperAdmin,
      onUpdate,
      navigate,
      totalCount,
      currentPage,
      pageSize,
      handleChannelChart,
      handlePlatformChart,
      handleReverseCarryover,
      editingPrev,
      handlePrevAmountEdit,
      editingAdvance,
      handleAdvanceAmountEdit,
    ]
  );

  return (
    <>
      <ResponsiveTable
        columns={columns}
        data={data}
        visibleOnMobile={['index', 'client.name', 'amount']}
      />

      {/* 연간 정산 모달 */}
      {selectedClient && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedClient(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedClient(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <h2 className="text-xl font-bold mb-4 text-white">
              "{selectedClient.clientName}" 최근 1년 정산 그래프
            </h2>
            {loadingChart ? (
              <ChartLoading />
            ) : (
              <Chart
                type="bar"
                width="100%"
                height="300"
                series={[{ name: '정산액', data: channelAmounts.map((item) => item.amount) }]}
                options={UNIFIED_BAR_OPTIONS(
                  channelAmounts.map((item) => item.month),
                  'client-channel-chart'
                )}
              />
            )}
          </div>
        </div>
      )}

      {/* 플랫폼 모달 */}
      {selectedPlatformClient && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setSelectedPlatformClient(null)}>
          <div
            className="bg-[#1a2b42] p-6 rounded-xl shadow-xl border border-white/10 w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
              onClick={() => setSelectedPlatformClient(null)}>
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">
                "{selectedPlatformClient.clientName}"{' '}
                {parseInt(selectedPlatformClient.calculateMonth.split('-')[1], 10)}
                월 플랫폼별 정산 그래프
              </h2>
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors mr-6"
                onClick={handleExcelDownload}
                title="앨범별/곡별 플랫폼 정산 엑셀 다운로드">
                <FontAwesomeIcon icon={faFileExcel} />
                엑셀 다운로드
              </button>
            </div>
            {loadingChart ? (
              <ChartLoading />
            ) : (() => {
              const grouped = groupTopPlatforms(platformAmounts);
              return (
                <Chart
                  type="bar"
                  width="100%"
                  height={Math.max(260, grouped.length * 42)}
                  series={[{ name: '정산액', data: grouped.map((item) => item.platformAmount) }]}
                  options={PLATFORM_HORIZ_OPTIONS(grouped.map((item) => item.platformType), 'client-platform-chart')}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}

export default ByClientLatestMonthTable;
